import {useEffect, useCallback, useState} from 'preact/hooks';

import {useProductDetails} from 'utilities/product';
import {BrandHandle, DeliveryEstimates} from 'utilities/delivery-promise';
import {Scenario} from 'utilities/promise-presenter';
import {ReportType, useErrorLogger} from 'utilities/error-logger';
import {scriptLoader} from 'utilities/script-loader';

export const useSDKLoader = (
  presentment: Scenario,
  deliveryEstimate: DeliveryEstimates | null,
) => {
  const {reportError} = useErrorLogger();
  const bwpID = 'generic-promise-wc-sdk';
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState<'initial' | 'loaded' | 'error'>(
    'initial',
  );
  const isBuyWithPrime =
    presentment && presentment.brandHandle === BrandHandle.Prime;

  const {variantId} = useProductDetails();

  const initialize = useCallback(() => {
    return window.BuyWithPrime.initialize(
      false,
      deliveryEstimate?.selectedShippingOption?.deliveryPromiseProviderAppId,
      variantId?.toString(),
      (error, message) =>
        reportError({
          severity: ReportType.Investigate,
          error,
          message,
        }),
    );
  }, [deliveryEstimate, reportError, variantId]);

  const SDKLoader = useCallback(
    (optimusPdpScriptUrl: string) => {
      return new Promise<boolean>((resolve) => {
        scriptLoader(optimusPdpScriptUrl, bwpID)
          .then(async () => {
            try {
              const result = await initialize();
              resolve(result === 'loaded');
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Failed to initialize Optimus SDK', error);
              reportError({
                severity: ReportType.Investigate,
                error,
                message: `Failed to initialize Optimus SDK`,
              });

              resolve(false);
            }
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load Optimus SDK', error);
            reportError({
              severity: ReportType.Investigate,
              error,
              message: `Failed to load Optimus SDK`,
            });
            resolve(false);
          });
      });
    },
    [initialize, reportError],
  );

  useEffect(() => {
    if (isBuyWithPrime && window.BuyWithPrime && variantId) {
      return window.BuyWithPrime.update(variantId.toString());
    }
  }, [isBuyWithPrime, variantId, status]);

  useEffect(() => {
    if (!isBuyWithPrime || status === 'loaded' || status === 'error') return;
    setIsLoading(true);
    SDKLoader(presentment.sdkURL ?? '')
      .then((loaded) => {
        setIsLoading(false);
        setStatus(loaded ? 'loaded' : 'error');
      })
      .catch(() => {
        setIsLoading(false);
        setStatus('error');
      });
  }, [SDKLoader, status, presentment, isBuyWithPrime]);

  return {
    isLoading,
    status,
  };
};
